import {
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  input,
  model,
  output,
  signal,
  viewChild
} from '@angular/core';
import { Subject } from 'rxjs';

import { ValueAccessorProviderFactory } from '../../value-accessor/value-accessor-provider-factory';
import { YearPickerComponent } from '../year-picker/year-picker.component';
import { MonthPickerComponent } from '../month-picker/month-picker.component';
import { DayPickerComponent } from '../day-picker/day-picker.component';
import { DatePickerInnerComponent } from '../date-picker-inner/date-picker-inner.component';
import { OverlayComponent } from '../../overlay/overlay.component';
import { OverlayService } from '../../overlay/overlay.service';
import { IdService } from '../../../services/id.service';
import { DatePickerService } from '../date-picker/date-picker.service';
import { DateService } from '../../../services/date/date.service';
import { DatePickerConfig } from '../date-picker/date-picker.config';
import { RangeSelectorCalendarConfig } from './range-selector-calendar.config';

import type { ControlValueAccessor } from '@angular/forms';
import type { DatePickerMode } from '../date-picker/date-picker-mode.enum';
import type { DateValidator } from '../date-picker/date-validator.type';
import type {
  CalendarStyleType,
  DateRange,
  DateRangeKeys,
  RangeDateChange
} from '../date-range-selector.domain';
import type { OutputRefSubscription,
  OnInit,
  Signal,
  TemplateRef,
  ModelSignal,
  OutputEmitterRef,
  WritableSignal
  , InputSignal  } from '@angular/core';

import type { DatePickerFormatType } from '../../../services/date/date-formatter.service';

import type { RangeSelectorCalendarInputLike } from './range-selector-calendar.contract';

/** Empty range used whenever the owner writes `null` / `undefined`. */
function emptyRange(): DateRange {
  return { startDate: undefined, endDate: undefined };
}

/**
 * The Range Selector Calendar Component
 *
 * This is the input for selecting range of dates.
 *
 * Follows the core dual-support control shape — see
 * `wiki/signal-forms/core-signal-forms-revised-dual-support-plan.md`.
 * `value` is the single source of truth and the Signal Forms native
 * custom-control binding; the ControlValueAccessor is a thin adapter over it.
 */
@Component({
  selector: 'td-wb-range-selector-calendar',
  templateUrl: './range-selector-calendar.component.html',
  styleUrls: ['./range-selector-calendar.component.scss'],
  providers: [
    RangeSelectorCalendarConfig,
    DatePickerService,
    DatePickerConfig,
    ValueAccessorProviderFactory(RangeSelectorCalendarComponent)
  ],
  imports: [OverlayComponent, DatePickerInnerComponent, DayPickerComponent, MonthPickerComponent, YearPickerComponent]
})
export class RangeSelectorCalendarComponent implements ControlValueAccessor, OnInit {
  private static readonly RANGE_PICKER_ID_PREFIX: string = 'td-wb-range-date-picker';

  // ── DI ──────────────────────────────────────────────────────────────────
  private readonly config = inject(RangeSelectorCalendarConfig);
  private readonly overlayService = inject(OverlayService);
  private readonly idService = inject(IdService);
  private readonly destroyRef = inject(DestroyRef);

  // ── Signal inputs ────────────────────────────────────────────────────────
  readonly calendarStyle: InputSignal<CalendarStyleType> = input<CalendarStyleType>('regular');

  /** Disabled state requested declaratively. */
  readonly disabledInput: InputSignal<boolean> = input<boolean>(false, { alias: 'disabled' });

  /** Disabled state pushed imperatively by a form owner through `setDisabledState()`. */
  private readonly disabledByOwner: WritableSignal<boolean> = signal(false);

  /**
   * Effective disabled state. Signal Forms writes both the `disabled` input and
   * `setDisabledState()` — reconcile, never pick one.
   */
  readonly disabled: Signal<boolean> = computed(
    (): boolean => this.disabledInput() || this.disabledByOwner()
  );

  /**
   * Selected range. Single source of truth, and the Signal Forms native
   * custom-control binding.
   */
  readonly value: ModelSignal<DateRange> = model<DateRange>(emptyRange());

  /** Signal Forms native custom-control touch notification. */
  readonly touch: OutputEmitterRef<void> = output<void>();

  // ── ViewChild ────────────────────────────────────────────────────────────
  readonly content: Signal<TemplateRef<unknown> | undefined> = viewChild<TemplateRef<unknown>>('rangePickerTemplate');
  readonly dateContent: Signal<TemplateRef<unknown> | undefined> = viewChild<TemplateRef<unknown>>('dateTemplate');

  // ── Config-derived props ──────────────────────────────────────────────────
  formatDay: DatePickerFormatType = this.config.formatDay as DatePickerFormatType;
  formatMonth: DatePickerFormatType = this.config.formatMonth as DatePickerFormatType;
  formatYear: DatePickerFormatType = this.config.formatYear as DatePickerFormatType;
  formatDayHeader: DatePickerFormatType = this.config.formatDayHeader as DatePickerFormatType;
  formatDayTitle: DatePickerFormatType = this.config.formatDayTitle as DatePickerFormatType;
  formatMonthTitle: DatePickerFormatType = this.config.formatMonthTitle as DatePickerFormatType;
  startingDay: number = this.config.startingDay;
  yearRange: number = this.config.yearRange;
  mode: DatePickerMode = this.config.mode;
  minMode: DatePickerMode = this.config.minMode as DatePickerMode;
  maxMode: DatePickerMode = this.config.maxMode as DatePickerMode;
  showWeeks: boolean = this.config.showWeeks;
  disabledDates: DateValidator;

  /**
   * Unique id set for each datepicker
   */
  uniqueId: Array<string> = [];

  startDateParsed: Date;
  endDateParsed: Date;

  isEndDateActive: boolean = false;
  isStartDateActive: boolean = false;

  readonly selectedChanged: Subject<DateRange> = new Subject<DateRange>();

  private dateRangePickerInputs: Map<DateRangeKeys, RangeSelectorCalendarInputLike>
    = new Map<DateRangeKeys, RangeSelectorCalendarInputLike>();

  private inputSubscriptions: Array<OutputRefSubscription> = [];

  /** Guards against the downward child sync echoing back as a user change. */
  private syncingToChildren: boolean = false;

  private onChangeCallback: (value: DateRange) => void = (): void => {};
  private onTouchedCallback: () => void = (): void => {};

  constructor() {
    // Push signal state into the projected child date inputs. This is the sanctioned
    // use of effect(): syncing signal state to an imperative child API. It covers
    // writes arriving via [(value)]; writeValue() and selection events also sync
    // synchronously so callers observe the children immediately.
    effect((): void => {
      this.syncChildren(this.value());
    });

    this.destroyRef.onDestroy(() => {
      this.inputSubscriptions.forEach((s: OutputRefSubscription): void => s.unsubscribe());
      this.selectedChanged.complete();
    });
  }

  /**
   * This function sets unique ids
   */
  ngOnInit(): void {
    this.uniqueId[0] = this.idService.generateNamespaceSequentialId(RangeSelectorCalendarComponent.RANGE_PICKER_ID_PREFIX);
    this.uniqueId[1] = this.idService.generateNamespaceSequentialId(RangeSelectorCalendarComponent.RANGE_PICKER_ID_PREFIX);

    // React to calendarStyle input changes
    const style = this.calendarStyle();
    if (style === 'regular') {
      this.isStartDateActive = false;
      this.isEndDateActive = false;
    }
    if (style === 'compact') {
      if (!this.isEndDateActive && !this.isStartDateActive) {
        this.isStartDateActive = true;
        this.isEndDateActive = false;
      }
    }
  }

  getFormat(key: DateRangeKeys): DatePickerFormatType {
    return this.dateRangePickerInputs[key] === undefined ? null : this.dateRangePickerInputs[key].format();
  }

  getMinDate(key: DateRangeKeys): Date | null {
    return this.dateRangePickerInputs[key] === undefined ? null : this.dateRangePickerInputs[key].minDate;
  }

  getMaxDate(key: DateRangeKeys): Date | null {
    return this.dateRangePickerInputs[key] === undefined ? null : this.dateRangePickerInputs[key].maxDate;
  }

  updateValue(change: RangeDateChange): void {
    if (change !== null && change !== undefined) {
      this.onPickerChange(change.value, change.field);
    }
  }

  onPickerChange(date: Date, input: 'startDate' | 'endDate'): void {
    // A disabled control must never produce a user change.
    if (this.disabled()) {
      return;
    }
    const parsed: string = input + 'Parsed';
    this[parsed] = date;
    this.normalizeRangeOrder(input);
    this.onValueChange();
  }

  close(): void {
    this.overlayService.close();
  }

  disabledDateInput(key: DateRangeKeys): DateValidator {
    if (!this._disabledDateInputs[key]) {
      this._disabledDateInputs[key] =
        (date: Date): boolean => this.dateRangePickerInputs[key]?.isDisabledDate(date) ?? true;
    }
    return this._disabledDateInputs[key];
  }

  // Stable per-key validator function references — avoid new function identities
  // every CD pass which would cause infinite change detection in OnPush children
  // bound via signal inputs.
  private _disabledDateInputs: Partial<Record<DateRangeKeys, DateValidator>> = {};

  registerInput(input: RangeSelectorCalendarInputLike, key: DateRangeKeys): void {
    this.dateRangePickerInputs[key] = input;
    const inputSubscription: OutputRefSubscription =
      input.valueChange.subscribe((change: RangeDateChange | null): void => {
        this.updateValue(change);
      });
    this.inputSubscriptions.push(inputSubscription);
  }

  setActiveCalendar(key: DateRangeKeys): void {
    this.isStartDateActive = key === 'startDate';
    this.isEndDateActive = !this.isStartDateActive;
  }

  getActiveCalendarKey(): string {
    if (this.isStartDateActive) {
      return 'startDate';
    }
    if (this.isEndDateActive) {
      return 'endDate';
    }
  }

  // ── ControlValueAccessor ──────────────────────────────────────────────────

  /** Owner -> control. Must never emit onChangeCallback. */
  writeValue(value: DateRange | null | undefined): void {
    const next: DateRange = value ?? emptyRange();
    this.value.set(next);
    // Sync synchronously so callers observe the child inputs immediately;
    // the effect() covers the same for writes arriving via [(value)].
    this.syncChildren(next);
  }

  /** Control -> owner. */
  registerOnChange(fn: (value: DateRange) => void): void {
    this.onChangeCallback = fn;
  }

  /** Control -> owner. */
  registerOnTouched(fn: () => void): void {
    this.onTouchedCallback = fn;
  }

  /** Owner -> control. */
  setDisabledState(isDisabled: boolean): void {
    this.disabledByOwner.set(isDisabled);
  }

  /** Pushes the given range down into the projected child date inputs. */
  private syncChildren(range: DateRange): void {
    this.syncingToChildren = true;
    try {
      this.startDateParsed = range.startDate;
      this.endDateParsed = range.endDate;
      this.selectedChanged.next(range);
    } finally {
      this.syncingToChildren = false;
    }
  }

  private onValueChange(): void {
    // Re-entrant call triggered by our own downward sync — not a user change.
    if (this.syncingToChildren) {
      return;
    }
    const customRange: DateRange = {
      startDate: this.startDateParsed,
      endDate: this.endDateParsed
    };
    this.value.set(customRange);
    this.selectedChanged.next(customRange);
    this.onChangeCallback(customRange);
    this.onTouchedCallback();
    this.touch.emit();
  }

  private normalizeRangeOrder(changedField: 'startDate' | 'endDate'): void {
    if (!DateService.isDate(this.startDateParsed) || !DateService.isDate(this.endDateParsed)) {
      return;
    }

    if (this.startDateParsed <= this.endDateParsed) {
      return;
    }

    if (changedField === 'startDate') {
      this.endDateParsed = this.startDateParsed;
      return;
    }

    const yearBeforeEndDate = DateService.getYearBefore(this.endDateParsed) ?? this.endDateParsed;
    this.startDateParsed = yearBeforeEndDate <= this.endDateParsed ? yearBeforeEndDate : this.endDateParsed;
  }
}
